import React from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Breadcrumbs,
  Link,
  Chip,
  Divider,
  Button,
  AlertTitle,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Checkbox,
  Badge,
  Fab
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import WarningIcon from '@mui/icons-material/Warning';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckIcon from '@mui/icons-material/Check';
import BaseDialog from './BaseDialog';
import BaseButton from './BaseButton';
import graphService from '../../services/graphService';

/**
 * Dialog pour sélectionner des fichiers depuis OneDrive via Microsoft Graph API
 * @param {boolean} open - État d'ouverture du dialog
 * @param {function} onClose - Callback de fermeture
 * @param {function} onFileSelect - Callback appelé quand un fichier est sélectionné
 */
const OneDriveFilePickerDialog = ({ open, onClose, onFileSelect }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [authError, setAuthError] = React.useState(false);
  const [files, setFiles] = React.useState([]);
  const [currentFolder, setCurrentFolder] = React.useState(null);
  const [breadcrumbs, setBreadcrumbs] = React.useState([{ name: 'OneDrive', id: 'root' }]);
  const [connectionStatus, setConnectionStatus] = React.useState('checking'); // 'checking', 'connected', 'disconnected'
  const [sortBy, setSortBy] = React.useState('name'); // 'name', 'createdDate', 'modifiedDate'
  const [sortOrder, setSortOrder] = React.useState('asc'); // 'asc', 'desc'
  const [isPullToRefresh, setIsPullToRefresh] = React.useState(false);
  const [pullDistance, setPullDistance] = React.useState(0);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [multiSelectMode, setMultiSelectMode] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { addSystemNotification } = useNotifications();

  // Formats de fichiers supportés
  const supportedFormats = ['.mp3', '.wav', '.m4a', '.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];

  // Fonction de formatage des dates en format relatif
  const formatRelativeDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays === 0) {
      return "Aujourd'hui";
    } else if (diffDays === 1) {
      return "Hier";
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  };

  // Fonction de formatage de date complète
  const formatFullDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction de tri des fichiers
  const sortFiles = (filesArray) => {
    const folders = filesArray.filter(item => item.folder);
    const files = filesArray.filter(item => !item.folder);
    
    const sortFunction = (a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'createdDate':
          valueA = new Date(a.createdDateTime || 0);
          valueB = new Date(b.createdDateTime || 0);
          break;
        case 'modifiedDate':
          valueA = new Date(a.lastModifiedDateTime || 0);
          valueB = new Date(b.lastModifiedDateTime || 0);
          break;
        case 'name':
        default:
          valueA = a.name.toLowerCase();
          valueB = b.name.toLowerCase();
          break;
      }
      
      if (sortOrder === 'desc') {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      } else {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      }
    };
    
    // Trier les dossiers et les fichiers séparément
    const sortedFolders = [...folders].sort(sortFunction);
    const sortedFiles = [...files].sort(sortFunction);
    
    return [...sortedFolders, ...sortedFiles];
  };

  // Références pour le pull-to-refresh
  const containerRef = React.useRef(null);
  const startY = React.useRef(0);
  const currentY = React.useRef(0);

  // Gestion du pull-to-refresh (mobile uniquement)
  const handleTouchStart = (e) => {
    if (!isMobile || connectionStatus !== 'connected') return;
    
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) return;
    
    startY.current = e.touches[0].clientY;
    setIsPullToRefresh(false);
  };

  const handleTouchMove = (e) => {
    if (!isMobile || connectionStatus !== 'connected') return;
    
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) return;
    
    currentY.current = e.touches[0].clientY;
    const distance = Math.max(0, currentY.current - startY.current);
    
    if (distance > 0) {
      e.preventDefault();
      setPullDistance(Math.min(distance, 100));
      setIsPullToRefresh(distance > 60);
    }
  };

  const handleTouchEnd = async () => {
    if (!isMobile || connectionStatus !== 'connected') return;
    
    if (isPullToRefresh && pullDistance > 60) {
      await loadFiles(currentFolder);
    }
    
    // Animation de retour
    setPullDistance(0);
    setIsPullToRefresh(false);
    startY.current = 0;
    currentY.current = 0;
  };

  // Vérifier l'accès OneDrive au moment de l'ouverture
  React.useEffect(() => {
    if (open) {
      checkOneDriveConnection();
    }
  }, [open]);

  // Re-trier les fichiers quand les paramètres de tri changent
  React.useEffect(() => {
    if (files.length > 0) {
      setFiles(prevFiles => sortFiles(prevFiles));
    }
  }, [sortBy, sortOrder]);

  const checkOneDriveConnection = async (retryCount = 0) => {
    setLoading(true);
    setError(null);
    setAuthError(false);
    setConnectionStatus('checking');

    try {
      const accessCheck = await graphService.checkOneDriveAccess();

      if (accessCheck.success) {
        setConnectionStatus('connected');
        await loadFiles('root');
      } else {
        if (accessCheck.needsReconnection && retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return checkOneDriveConnection(1);
        }

        addSystemNotification('❌ Problème de connexion OneDrive:', 'error');
        setConnectionStatus('disconnected');
        setAuthError(accessCheck.needsReconnection);

        let userFriendlyError;
        if (accessCheck.needsReconnection) {
          if (accessCheck.error?.includes('Aucun compte Microsoft') || accessCheck.error?.includes('Aucun compte Azure')) {
            userFriendlyError = 'Aucun compte Microsoft connecté. Veuillez vous déconnecter puis vous reconnecter avec votre compte Microsoft pour accéder à OneDrive.';
          } else {
            userFriendlyError = 'Session Microsoft expirée. Veuillez actualiser la page (F5) ou vous reconnecter pour accéder à OneDrive.';
          }
        } else if (accessCheck.error?.includes('timeout')) {
          userFriendlyError = 'Délai d\'attente dépassé lors de la connexion à OneDrive. Vérifiez votre connexion internet et réessayez.';
        } else if (accessCheck.error?.includes('network')) {
          userFriendlyError = 'Problème de réseau. Vérifiez votre connexion internet et réessayez.';
        } else {
          userFriendlyError = accessCheck.error || 'Erreur de connexion à OneDrive. Réessayez dans quelques instants.';
        }

        setError(userFriendlyError);
      }
    } catch (error) {
      console.error('[OneDrive] ❌ Erreur lors de la vérification:', error);

      const isAuthError = error.message?.includes('Token') ||
                         error.message?.includes('compte') ||
                         error.message?.includes('Session');

      if (isAuthError && retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return checkOneDriveConnection(1);
      }

      addSystemNotification('❌ Erreur lors de la vérification OneDrive:', 'error');
      setConnectionStatus('disconnected');
      setAuthError(true);

      // Message d'erreur plus actionnable
      if (error.message?.includes('Aucun compte Microsoft') || error.message?.includes('Aucun compte Azure')) {
        setError('Aucun compte Microsoft connecté. Veuillez vous déconnecter puis vous reconnecter avec votre compte Microsoft pour accéder à OneDrive.');
      } else if (error.message?.includes('Session Microsoft expirée') || error.message?.includes('Token Azure')) {
        setError('Session Microsoft expirée. Veuillez actualiser la page (F5) ou vous reconnecter pour accéder à OneDrive.');
      } else {
        setError('Impossible de se connecter à OneDrive. Veuillez actualiser la page (F5) ou vous reconnecter avec votre compte Microsoft.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async (folderId) => {
    setLoading(true);
    setError(null);
    setAuthError(false);
    
    try {
      let filesList;
      if (folderId === 'root') {
        filesList = await graphService.listRootFiles();
      } else {
        filesList = await graphService.listFolderFiles(folderId);
      }
      
      // Séparer les dossiers et fichiers, filtrer les fichiers supportés
      const folders = filesList.filter(item => item.folder);
      const supportedFiles = filesList.filter(item => 
        item.file && supportedFormats.some(format => 
          item.name.toLowerCase().endsWith(format)
        )
      );
      
      // Combiner et trier les fichiers
      const allItems = [...folders, ...supportedFiles];
      const sortedItems = sortFiles(allItems);
      setFiles(sortedItems);
      setCurrentFolder(folderId);
      setConnectionStatus('connected');
      setLoading(false);
    } catch (error) {
      console.error('[OneDrive] Erreur lors du chargement des fichiers:', error);
      addSystemNotification('❌ Erreur lors du chargement des fichiers OneDrive:', 'error');

      // Détecter si c'est une erreur d'authentification
      const isAuthError = error.message?.includes('Token Azure non disponible') ||
                         error.message?.includes('Aucun compte Microsoft') ||
                         error.message?.includes('Session Microsoft expirée') ||
                         error.message?.includes('authentication') ||
                         error.message?.includes('Aucun compte Azure') ||
                         error.response?.status === 401;

      setAuthError(isAuthError);
      setConnectionStatus('disconnected');

      if (isAuthError) {
        // Messages d'erreur plus spécifiques et actionnables
        if (error.message?.includes('Aucun compte Microsoft') || error.message?.includes('Aucun compte Azure')) {
          setError('Aucun compte Microsoft connecté. Veuillez vous déconnecter puis vous reconnecter avec votre compte Microsoft pour accéder à OneDrive.');
        } else if (error.message?.includes('Session Microsoft expirée')) {
          setError('Votre session Microsoft a expiré. Veuillez actualiser la page (F5) ou vous reconnecter pour accéder à OneDrive.');
        } else {
          setError('Session expirée. Veuillez actualiser la page (F5) ou cliquer sur "Se déconnecter" puis vous reconnecter avec votre compte Microsoft.');
        }
      } else if (error.message?.includes('timeout')) {
        setError('Délai d\'attente lors du chargement des fichiers OneDrive. Vérifiez votre connexion et réessayez.');
      } else if (error.message?.includes('network')) {
        setError('Problème de réseau lors du chargement. Vérifiez votre connexion internet.');
      } else if (error.message?.includes('403')) {
        setError('Accès refusé à OneDrive. Vérifiez vos permissions ou contactez votre administrateur.');
      } else {
        setError(`Impossible de charger les fichiers OneDrive: ${error.message}. Réessayez dans quelques instants ou actualisez la page (F5).`);
      }

      setLoading(false);
    }
  };

  const handleFolderClick = (folder) => {
    const newBreadcrumbs = [...breadcrumbs, { name: folder.name, id: folder.id }];
    setBreadcrumbs(newBreadcrumbs);
    setSelectedFiles([]); // Reset selection when changing folder
    loadFiles(folder.id);
  };

  const handleBreadcrumbClick = (breadcrumb, index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);
    setSelectedFiles([]); // Reset selection when changing folder
    loadFiles(breadcrumb.id);
  };

  const handleFileSelect = async (file) => {
    if (multiSelectMode) {
      // Mode sélection multiple : toggle la sélection
      setSelectedFiles(prev => {
        const isSelected = prev.find(f => f.id === file.id);
        if (isSelected) {
          return prev.filter(f => f.id !== file.id);
        } else {
          return [...prev, file];
        }
      });
    } else {
      // Mode sélection simple : sélectionner immédiatement
      try {
        setLoading(true);
        const downloadUrl = await graphService.getDownloadUrl(file.id);
        
        const fileInfo = {
          id: file.id,
          name: file.name,
          downloadUrl: downloadUrl,
          size: file.size,
          lastModified: file.lastModifiedDateTime,
          mimeType: file.file?.mimeType || 'audio/mpeg',
          type: file.file?.mimeType || 'audio/mpeg'
        };
        
        onFileSelect(fileInfo);
        onClose();
      } catch (error) {
        console.error('[OneDrive] Erreur lors de la récupération du fichier:', error);
        addSystemNotification('❌ Erreur lors de la récupération du fichier:', 'error');

        const isAuthError = error.message?.includes('Token Azure non disponible') ||
                           error.message?.includes('Aucun compte Microsoft') ||
                           error.message?.includes('Session Microsoft expirée') ||
                           error.message?.includes('authentication') ||
                           error.message?.includes('Aucun compte Azure') ||
                           error.response?.status === 401;

        if (isAuthError) {
          setAuthError(true);
          if (error.message?.includes('Aucun compte Microsoft') || error.message?.includes('Aucun compte Azure')) {
            setError('Aucun compte Microsoft connecté. Veuillez vous déconnecter puis vous reconnecter avec votre compte Microsoft pour accéder à ce fichier.');
          } else {
            setError('Session Microsoft expirée. Veuillez actualiser la page (F5) ou vous reconnecter pour accéder à ce fichier.');
          }
        } else {
          setError(`Impossible de récupérer le fichier: ${error.message}`);
        }
        
        setLoading(false);
      }
    }
  };

  const handleConfirmMultiSelection = async () => {
    if (selectedFiles.length === 0) return;
    
    try {
      setLoading(true);
      const filesInfo = [];
      
      for (const file of selectedFiles) {
        const downloadUrl = await graphService.getDownloadUrl(file.id);
        filesInfo.push({
          id: file.id,
          name: file.name,
          downloadUrl: downloadUrl,
          size: file.size,
          lastModified: file.lastModifiedDateTime,
          mimeType: file.file?.mimeType || 'audio/mpeg',
          type: file.file?.mimeType || 'audio/mpeg'
        });
      }
      
      onFileSelect(filesInfo); // Envoyer le tableau de fichiers
      onClose();
    } catch (error) {
      addSystemNotification('❌ Erreur lors de la récupération des fichiers:', 'error');
      setError(`Impossible de récupérer les fichiers: ${error.message}`);
      setLoading(false);
    }
  };

  const handleReconnectAzure = () => {
    // Fermer le dialog OneDrive et déclencher une reconnexion Azure
    onClose();
    
    // Déclencher l'événement pour forcer la reconnexion
    window.dispatchEvent(new CustomEvent('mobile-auth-error', {
      detail: {
        type: 'onedrive_auth_required',
        message: 'Reconnexion Azure requise pour OneDrive',
        action: 'reconnect'
      }
    }));
  };

  const getFileIcon = (file) => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a')) {
      return <AudioFileIcon sx={{ color: '#FF6B35' }} />;
    } else if (name.endsWith('.mp4') || name.endsWith('.avi') || name.endsWith('.mov') || 
               name.endsWith('.wmv') || name.endsWith('.flv') || name.endsWith('.webm') || 
               name.endsWith('.mkv')) {
      return <VideoFileIcon sx={{ color: '#4285F4' }} />;
    }
    return <InsertDriveFileIcon sx={{ color: '#757575' }} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Actions du dialog
  const dialogActions = (
    <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'space-between' }}>
      <BaseButton 
        onClick={onClose} 
        variant="outlined"
        sx={{ px: 4, py: 1 }}
      >
        Annuler
      </BaseButton>
      
      <Box sx={{ display: 'flex', gap: 2 }}>
        {connectionStatus === 'connected' && files.filter(f => !f.folder).length > 0 && !multiSelectMode && (
          <BaseButton 
            onClick={() => setMultiSelectMode(true)}
            variant="outlined"
            startIcon={<CheckBoxIcon />}
            sx={{ px: 3, py: 1 }}
          >
            Sélection multiple
          </BaseButton>
        )}

        {multiSelectMode && (
          <>
            <BaseButton 
              onClick={() => {
                setMultiSelectMode(false);
                setSelectedFiles([]);
              }}
              variant="outlined"
              sx={{ px: 3, py: 1 }}
            >
              Annuler sélection
            </BaseButton>
            <BaseButton 
              onClick={handleConfirmMultiSelection}
              variant="contained"
              disabled={selectedFiles.length === 0}
              startIcon={<CheckIcon />}
              sx={{ 
                px: 3, 
                py: 1,
                background: selectedFiles.length > 0 ? 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)' : undefined
              }}
            >
              Confirmer ({selectedFiles.length})
            </BaseButton>
          </>
        )}
        
        {connectionStatus === 'disconnected' && (
          <BaseButton 
            onClick={checkOneDriveConnection}
            variant="outlined"
            startIcon={<RefreshIcon />}
            sx={{ px: 3, py: 1 }}
          >
            Réessayer
          </BaseButton>
        )}
        
        {authError && (
          <BaseButton 
            onClick={handleReconnectAzure}
            variant="contained"
            startIcon={<LoginIcon />}
            sx={{ 
              px: 3, 
              py: 1,
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #F57C00 0%, #EF6C00 100%)',
              }
            }}
          >
            Se reconnecter
          </BaseButton>
        )}
        
        {connectionStatus === 'connected' && currentFolder !== 'root' && !multiSelectMode && (
          <BaseButton 
            onClick={() => handleBreadcrumbClick(breadcrumbs[breadcrumbs.length - 2], breadcrumbs.length - 2)}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ px: 4, py: 1 }}
          >
            Retour
          </BaseButton>
        )}
      </Box>
    </Box>
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={multiSelectMode ? `☁️ OneDrive - Sélection multiple (${selectedFiles.length})` : "☁️ OneDrive"}
      subtitle={multiSelectMode ? "Cochez les fichiers à ajouter puis cliquez sur Confirmer" : "Sélectionnez un ou plusieurs fichiers audio/vidéo depuis votre OneDrive"}
      maxWidth="md"
      actions={dialogActions}
      customBackgroundContent="#f8faff"
    >
      <Box 
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{ 
          background: 'linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(248,250,255,0.9) 100%)',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.3)',
          backdropFilter: 'blur(10px)',
          minHeight: '400px',
          overflowY: 'auto',
          position: 'relative',
          transform: isMobile && pullDistance > 0 ? `translateY(${pullDistance * 0.3}px)` : 'none',
          transition: pullDistance === 0 ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
        }}
      >
        {/* Indicateur pull-to-refresh (mobile uniquement) */}
        {isMobile && connectionStatus === 'connected' && pullDistance > 10 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: `${Math.min(pullDistance * 0.2, 20)}px 16px 10px 16px`,
              background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(248,250,255,0.95) 100%)',
              borderRadius: '0 0 20px 20px',
              marginBottom: 2,
              transition: pullDistance === 0 ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              transform: `scale(${Math.min(pullDistance / 60, 1)})`,
              transition: 'transform 0.1s ease'
            }}>
              <CircularProgress 
                size={20} 
                sx={{ 
                  color: isPullToRefresh ? '#4CAF50' : '#FF9800',
                  transform: `rotate(${pullDistance * 3.6}deg)`,
                  transition: 'color 0.3s ease'
                }} 
              />
              <Typography variant="body2" sx={{ 
                color: isPullToRefresh ? '#4CAF50' : '#FF9800',
                fontWeight: 600,
                fontSize: '0.9rem',
                transition: 'color 0.3s ease',
                textAlign: 'center',
                minWidth: '180px'
              }}>
                {isPullToRefresh ? '🔄 Relâcher pour actualiser' : '⬇️ Tirer pour actualiser'}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Statut de connexion */}
        {connectionStatus === 'checking' && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress 
              size={48} 
              sx={{ 
                mb: 2,
                color: '#3F51B5'
              }} 
            />
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#3F51B5' }}>
              Vérification de la connexion OneDrive...
            </Typography>
          </Box>
        )}

        {/* Erreur d'authentification */}
        {authError && connectionStatus === 'disconnected' && (
          <Box sx={{ p: 3 }}>
            <Alert 
              severity="warning" 
              sx={{ 
                borderRadius: '12px',
                '& .MuiAlert-message': {
                  fontWeight: 500
                }
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleReconnectAzure}
                  startIcon={<LoginIcon />}
                >
                  Se reconnecter
                </Button>
              }
            >
              <AlertTitle>Authentification requise</AlertTitle>
              {error || 'Votre session Azure a expiré. Reconnectez-vous pour accéder à OneDrive.'}
            </Alert>
          </Box>
        )}

        {/* Erreur générale */}
        {error && !authError && (
          <Box sx={{ p: 3 }}>
            <Alert 
              severity="error" 
              sx={{ 
                borderRadius: '12px',
                '& .MuiAlert-message': {
                  fontWeight: 500
                }
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={checkOneDriveConnection}
                  startIcon={<RefreshIcon />}
                >
                  Réessayer
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* Interface normale - Navigation breadcrumbs */}
        {connectionStatus === 'connected' && (
          <>
            <Box sx={{ p: 3, pb: 1 }}>
              <Breadcrumbs separator="›" sx={{ mb: 2 }}>
                {breadcrumbs.map((breadcrumb, index) => (
                  <Link
                    key={breadcrumb.id}
                    component="button"
                    variant="body2"
                    onClick={() => handleBreadcrumbClick(breadcrumb, index)}
                    sx={{ 
                      color: index === breadcrumbs.length - 1 ? '#3F51B5' : '#757575',
                      fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {index === 0 && <HomeIcon sx={{ fontSize: 16 }} />}
                    {breadcrumb.name}
                  </Link>
                ))}
              </Breadcrumbs>
              
              <Chip 
                label={`${files.length} élément${files.length > 1 ? 's' : ''}`}
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(63, 81, 181, 0.1)',
                  color: '#3F51B5',
                  fontWeight: 500
                }}
              />
            </Box>

            {/* Barre de contrôles de tri */}
            <Box sx={{ 
              px: 3, 
              py: 2,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: '#757575', fontWeight: 500 }}>
                  Trier par :
                </Typography>
                
                <ToggleButtonGroup
                  value={sortBy}
                  exclusive
                  onChange={(_, newSortBy) => {
                    if (newSortBy !== null) {
                      setSortBy(newSortBy);
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(63, 81, 181, 0.2)',
                      color: '#757575',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(63, 81, 181, 0.1)',
                        color: '#3F51B5',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: 'rgba(63, 81, 181, 0.15)',
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(63, 81, 181, 0.05)',
                      }
                    }
                  }}
                >
                  <ToggleButton value="name">
                    <SortByAlphaIcon sx={{ fontSize: 16, mr: 1 }} />
                    Nom
                  </ToggleButton>
                  <ToggleButton value="createdDate">
                    <CalendarTodayIcon sx={{ fontSize: 16, mr: 1 }} />
                    Création
                  </ToggleButton>
                  <ToggleButton value="modifiedDate">
                    <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />
                    Modification
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Bouton de rafraîchissement (desktop uniquement) */}
                {!isMobile && (
                  <Tooltip title="Actualiser les fichiers">
                    <span>
                      <IconButton
                        onClick={() => loadFiles(currentFolder)}
                        size="small"
                        disabled={loading}
                        sx={{
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          color: '#388E3C',
                          '&:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.15)',
                            transform: 'rotate(180deg)',
                          },
                          '&:disabled': {
                            backgroundColor: 'rgba(158, 158, 158, 0.1)',
                            color: '#9E9E9E',
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                
                <Tooltip title={`Tri ${sortOrder === 'asc' ? 'croissant' : 'décroissant'}`}>
                  <IconButton
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(63, 81, 181, 0.1)',
                      color: '#3F51B5',
                      '&:hover': {
                        backgroundColor: 'rgba(63, 81, 181, 0.15)',
                        transform: 'rotate(180deg)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider sx={{ mx: 3, borderColor: 'rgba(63, 81, 181, 0.1)' }} />
          </>
        )}

        {/* Chargement des fichiers */}
        {loading && connectionStatus === 'connected' && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress 
              size={48} 
              sx={{ 
                mb: 2,
                color: '#3F51B5'
              }} 
            />
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#3F51B5' }}>
              Chargement des fichiers...
            </Typography>
          </Box>
        )}

        {/* Liste des fichiers */}
        {!loading && connectionStatus === 'connected' && files.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <InsertDriveFileIcon 
              sx={{ 
                fontSize: 48, 
                color: '#BDBDBD',
                mb: 2
              }} 
            />
            <Typography variant="h6" sx={{ color: '#757575', mb: 1 }}>
              Aucun fichier trouvé
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ce dossier ne contient aucun fichier audio ou vidéo supporté.
            </Typography>
          </Box>
        )}

        {!loading && connectionStatus === 'connected' && files.length > 0 && (
          <List sx={{ px: 1, pb: 2 }}>
            {files.map((item) => {
              const isSelected = selectedFiles.find(f => f.id === item.id);
              const isFile = !item.folder;
              
              return (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    onClick={() => item.folder ? handleFolderClick(item) : handleFileSelect(item)}
                    sx={{
                      borderRadius: '12px',
                      mx: 2,
                      my: 0.5,
                      backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                      border: isSelected ? '2px solid #4CAF50' : 'none',
                      '&:hover': {
                        backgroundColor: isSelected ? 'rgba(76, 175, 80, 0.15)' : 'rgba(63, 81, 181, 0.08)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(63, 81, 181, 0.15)'
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {/* Checkbox en mode sélection multiple pour les fichiers */}
                    {multiSelectMode && isFile && (
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Checkbox
                          checked={!!isSelected}
                          sx={{
                            color: '#4CAF50',
                            '&.Mui-checked': {
                              color: '#4CAF50',
                            }
                          }}
                        />
                      </ListItemIcon>
                    )}
                    
                    {/* Icône normale */}
                    {(!multiSelectMode || item.folder) && (
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.folder ? 
                          <FolderIcon sx={{ color: '#FFC107' }} /> : 
                          getFileIcon(item)
                        }
                      </ListItemIcon>
                    )}
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {item.folder && (
                            <Chip 
                              label="Dossier" 
                              size="small" 
                              sx={{ 
                                fontSize: '0.7rem',
                                height: 20,
                                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                color: '#F57C00'
                              }} 
                            />
                          )}
                          {item.file && (
                            <>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(item.size)}
                              </Typography>
                              <Chip 
                                label="Fichier" 
                                size="small" 
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: 20,
                                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                  color: '#388E3C'
                                }} 
                              />
                            </>
                          )}
                        </Box>
                        
                        {/* Affichage des dates */}
                        <Tooltip 
                          title={
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                📅 Créé : {formatFullDate(item.createdDateTime)}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                ⏱️ Modifié : {formatFullDate(item.lastModifiedDateTime)}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="bottom-start"
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                            <CalendarTodayIcon sx={{ fontSize: 12, color: '#9E9E9E' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {sortBy === 'createdDate' ? (
                                `Créé ${formatRelativeDate(item.createdDateTime)}`
                              ) : (
                                `Modifié ${formatRelativeDate(item.lastModifiedDateTime)}`
                              )}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        )}

        {/* Informations sur les formats supportés */}
        {connectionStatus === 'connected' && !loading && (
          <Box sx={{ 
            mx: 3,
            mb: 3,
            background: 'rgba(63, 81, 181, 0.05)',
            border: '1px solid rgba(63, 81, 181, 0.1)',
            borderRadius: '12px',
            p: 2
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              📎 <strong>Formats supportés :</strong> MP3, WAV, M4A, MP4, AVI, MOV, WMV, FLV, WebM, MKV
            </Typography>
          </Box>
        )}

        {/* FAB mobile pour sélection multiple */}
        {isMobile && multiSelectMode && selectedFiles.length > 0 && (
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 20,
              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #388E3C 0%, #2E7D32 100%)',
              },
              zIndex: 1300
            }}
            onClick={handleConfirmMultiSelection}
          >
            <Badge badgeContent={selectedFiles.length} color="secondary">
              <CheckIcon />
            </Badge>
          </Fab>
        )}
      </Box>
    </BaseDialog>
  );
};

export default OneDriveFilePickerDialog; 